import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { EcoCard, EcoCardComponent } from './eco-card.component';
import { EcoIconComponent, EcoIconName } from './eco-icon.component';
import { fadeUp, headIn, stageIn, staggerIn, stepsIn } from './ecosystem.animations';

/** A satellite placed in the stage grid; `slot` is the grid area it occupies. */
interface EcoSat {
  card: EcoCard;
  slot: string;
}

/** One measured hub -> card connector, in stage pixel space. */
interface EcoLink {
  id: string;
  d: string;
  tone: string;
  delay: string;
}

interface EcoTrust {
  id: string;
  lead: string;
  label: string;
  icon: EcoIconName;
  tone: 'pink' | 'violet' | 'blue';
}

@Component({
  selector: 'app-ecosystem',
  standalone: true,
  imports: [CommonModule, EcoCardComponent, EcoIconComponent],
  templateUrl: './ecosystem.component.html',
  styleUrls: ['./ecosystem.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeUp, headIn, staggerIn, stageIn, stepsIn],
})
export class EcosystemComponent implements AfterViewInit, OnDestroy {
  @ViewChild('ecoRoot') ecoRoot?: ElementRef<HTMLElement>;
  @ViewChild('stage') stage?: ElementRef<HTMLElement>;
  @ViewChild('hub') hub?: ElementRef<HTMLElement>;
  @ViewChildren('sat') satRefs?: QueryList<ElementRef<HTMLElement>>;

  /** Flips once when the section scrolls into view. */
  readonly viewState = signal<'hidden' | 'shown'>('hidden');
  reduced = false;

  /** Connector paths, recomputed from real geometry whenever the stage resizes. */
  readonly links = signal<EcoLink[]>([]);
  /** Stage size, so the SVG viewBox matches its box 1:1 and strokes stay true. */
  readonly stageBox = signal({ w: 0, h: 0 });
  /** Card currently hovered — highlights its connector. */
  readonly hot = signal<string | null>(null);

  readonly services: EcoCard[] = [
    { id: 'web', title: 'Web Development', desc: 'Fast, scalable web experiences', icon: 'web', tone: 'pink' },
    { id: 'mobile', title: 'Mobile Apps', desc: 'Powerful iOS & Android solutions', icon: 'mobile', tone: 'violet' },
    { id: 'software', title: 'Custom Software', desc: 'Built around your business', icon: 'software', tone: 'violet' },
    { id: 'uiux', title: 'UI/UX Design', desc: 'Simple, engaging user journeys', icon: 'uiux', tone: 'pink' },
    { id: 'marketing', title: 'Digital Marketing', desc: 'Reach, engage and convert', icon: 'marketing', tone: 'pink' },
  ];

  readonly apiCard: EcoCard = {
    id: 'api', title: 'API Integration', desc: 'Connect tools and data', icon: 'api', tone: 'violet',
  };
  readonly cloudCard: EcoCard = {
    id: 'cloud', title: 'Cloud & DevOps', desc: 'Secure, reliable infrastructure', icon: 'cloud', tone: 'blue',
  };
  readonly stackCard: EcoCard = {
    id: 'stack', title: 'Technology Stack', desc: 'Modern, secure and future-ready', icon: 'stack', tone: 'violet',
  };
  readonly scalableCard: EcoCard = {
    id: 'scalable', title: 'Scalable Solutions', desc: 'Flexible, reliable and built to grow', icon: 'scalable', tone: 'blue',
  };
  readonly deliveryCard: EcoCard = {
    id: 'delivery', title: 'Delivery Orchestration', desc: 'Aligned teams. Efficient delivery.', icon: 'delivery', tone: 'violet',
  };

  /**
   * The ten satellites, laid out around the hub. Each `slot` is a named grid
   * area — the ring reads clockwise from the top-left on desktop and collapses
   * to a plain list on small screens.
   */
  readonly satellites: EcoSat[] = [
    { card: this.services[0], slot: 'a' },
    { card: this.services[1], slot: 'b' },
    { card: this.services[2], slot: 'c' },
    { card: this.services[3], slot: 'd' },
    { card: this.apiCard, slot: 'e' },
    { card: this.cloudCard, slot: 'f' },
    { card: this.stackCard, slot: 'g' },
    { card: this.services[4], slot: 'h' },
    { card: this.deliveryCard, slot: 'i' },
    { card: this.scalableCard, slot: 'j' },
    // slots a-j ring the hub 4 / 2 / 4, mirrored top and bottom
  ];

  readonly platforms: string[] = ['Angular', 'React', 'Node.js', 'Laravel', 'Python', '.NET'];

  readonly benefits: EcoCard[] = [
    { id: 'faster', title: 'Faster Launch', desc: 'Go to market quickly', icon: 'rocket', tone: 'blue' },
    { id: 'connected', title: 'Connected Systems', desc: 'Seamless integrations', icon: 'nodes', tone: 'blue' },
    { id: 'better', title: 'Better Experiences', desc: 'Delight your users', icon: 'users', tone: 'blue' },
    { id: 'growth', title: 'Business Growth', desc: 'Sustainable results', icon: 'growth', tone: 'blue' },
  ];

  readonly steps: EcoCard[] = [
    { id: 'discover', title: 'Discover', desc: 'Understand needs and goals', icon: 'discover', tone: 'pink' },
    { id: 'design', title: 'Design', desc: 'Plan, prototype and define', icon: 'design', tone: 'pink' },
    { id: 'develop', title: 'Develop', desc: 'Build, test and iterate', icon: 'develop', tone: 'violet' },
    { id: 'launch', title: 'Launch', desc: 'Deploy with confidence', icon: 'rocket', tone: 'violet' },
    { id: 'scale', title: 'Scale', desc: 'Optimize and drive growth', icon: 'growth', tone: 'blue' },
  ];

  readonly trust: EcoTrust[] = [
    { id: 'secure', lead: '100%', label: 'Secure & Reliable', icon: 'shield', tone: 'pink' },
    { id: 'ontime', lead: 'On-Time', label: 'Delivery', icon: 'clock', tone: 'violet' },
    { id: 'client', lead: 'Client-First', label: 'Approach', icon: 'star', tone: 'violet' },
    { id: 'transparent', lead: 'Transparent', label: 'Communication', icon: 'users', tone: 'blue' },
    { id: 'results', lead: 'Results', label: 'That Matter', icon: 'growth', tone: 'blue' },
  ];

  private io?: IntersectionObserver;
  private ro?: ResizeObserver;
  private raf = 0;

  constructor(
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  trackById(_i: number, item: { id: string }): string {
    return item.id;
  }

  trackByName(_i: number, item: string): string {
    return item;
  }

  trackByLink(_i: number, item: EcoLink): string {
    return item.id;
  }

  trackBySat(_i: number, item: EcoSat): string {
    return item.card.id;
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.viewState.set('shown');
      return;
    }

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const el = this.ecoRoot?.nativeElement;
    if (this.reduced || !el) {
      this.viewState.set('shown');
    } else if (el.getBoundingClientRect().top < window.innerHeight * 0.85) {
      this.viewState.set('shown');
    } else {
      this.io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          this.zone.run(() => this.viewState.set('shown'));
          this.io?.disconnect();
        },
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      this.zone.runOutsideAngular(() => this.io?.observe(el));
    }

    this.watchStage();
  }

  ngOnDestroy(): void {
    this.io?.disconnect();
    this.ro?.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  /* ------------------------------------------------------------ geometry -- */

  /**
   * The connectors are measured rather than hard-coded: a fixed viewBox can
   * never line up with a fluid grid, which is why the old diagram's wires
   * floated free of the cards. Re-measures on every stage resize.
   */
  private watchStage(): void {
    const stage = this.stage?.nativeElement;
    if (!stage || typeof ResizeObserver === 'undefined') return;

    const schedule = () => {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        const next = this.measure();
        if (!next) return;
        this.zone.run(() => {
          this.stageBox.set(next.box);
          this.links.set(next.links);
        });
      });
    };

    this.zone.runOutsideAngular(() => {
      this.ro = new ResizeObserver(schedule);
      this.ro.observe(stage);
      schedule();
    });
  }

  private measure(): { box: { w: number; h: number }; links: EcoLink[] } | null {
    const stage = this.stage?.nativeElement;
    const hub = this.hub?.nativeElement;
    const sats = this.satRefs;
    if (!stage || !hub || !sats) return null;

    const sb = stage.getBoundingClientRect();
    if (sb.width < 40 || sb.height < 40) return null;

    // stacked layout (mobile): the ring is gone, so the wires would be nonsense
    if (getComputedStyle(stage).getPropertyValue('--eco-wires').trim() === 'off') {
      return { box: { w: Math.round(sb.width), h: Math.round(sb.height) }, links: [] };
    }

    const hb = hub.getBoundingClientRect();
    const cx = hb.left - sb.left + hb.width / 2;
    const cy = hb.top - sb.top + hb.height / 2;
    const rim = Math.min(hb.width, hb.height) / 2;

    const links: EcoLink[] = [];
    sats.forEach((ref, i) => {
      const el = ref.nativeElement;
      const b = el.getBoundingClientRect();
      if (!b.width) return;

      const left = b.left - sb.left;
      const top = b.top - sb.top;
      const mx = left + b.width / 2;
      const my = top + b.height / 2;
      const dx = cx - mx;
      const dy = cy - my;

      // anchor on whichever card edge actually faces the hub
      let ax: number;
      let ay: number;
      if (Math.abs(dx) * b.height >= Math.abs(dy) * b.width) {
        ax = dx > 0 ? left + b.width : left;
        ay = my;
      } else {
        ax = mx;
        ay = dy > 0 ? top + b.height : top;
      }

      // start the wire on the hub's rim, pointing at the anchor
      const len = Math.hypot(ax - cx, ay - cy) || 1;
      const hx = cx + ((ax - cx) / len) * rim;
      const hy = cy + ((ay - cy) / len) * rim;

      // a slight bow along the connector's normal keeps parallel wires apart;
      // anything larger swings the curve wide of the card it should reach
      const bow = Math.min(18, len * 0.07) * (i % 2 ? 1 : -1);
      const nx = -((ay - hy) / len) * bow;
      const ny = ((ax - hx) / len) * bow;
      const qx = (hx + ax) / 2 + nx;
      const qy = (hy + ay) / 2 + ny;

      links.push({
        id: el.dataset['id'] ?? String(i),
        tone: el.dataset['tone'] ?? 'violet',
        delay: `${(i * 0.34).toFixed(2)}s`,
        d: `M${hx.toFixed(1)},${hy.toFixed(1)} Q${qx.toFixed(1)},${qy.toFixed(1)} ${ax.toFixed(1)},${ay.toFixed(1)}`,
      });
    });

    return { box: { w: Math.round(sb.width), h: Math.round(sb.height) }, links };
  }
}
